<template>
  <fields-list
    :fields="fields"
    :editing-data="editingData"
    @update-field="updateField($event.value, $event.field)"
  />
</template>

<script>
import { listsMixin } from "@/mixins/listsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import FieldsList from "@/newComponents/StockManagement/FieldsList.vue";
import { unitConverterMixin } from "@/mixins/management/unitConverterMixin";

export default {
  name: "CableMaterialFields",

  components: {
    FieldsList,
  },

  mixins: [listsMixin, objectManagementMixin, unitConverterMixin],

  props: {
    /**
     * The specific fields values passed by the parent component
     */
    familySpecificFields: {
      type: Object,
      required: true
    },

    /**
     * Installation types for the cables
     */
    installationTypeOptions: {
      type: Array,
      required: true
    },

    /**
     * Loss value of the cable
     */
    lossValue: {},

    editingData: {
      type: Boolean,
      required: true,
    }
  },

  data() {
    return {
      /**
       * Cable type options for the user to choose
       */
      cableTypeOptions: null,
      /**
       * Logical subtype options for the user to choose
       */
      logicalSubtypeOptions: null
    };
  },

  /**
   * Executed when the component is mounted
   * It's a preemptive verification
   *
   */
  mounted() {
    this.loadLogicalSubtypeOptions();
    this.loadCableTypeOptions();
  },

  watch: {
    editingData() {
      this.requiredFieldsFilled();
    }
  },

  computed: {
    fields() {
      return [
        {
          label: this.$i18n.tc("Cable", 1) + ' ' + this.$i18n.tc("Type", 1),
          value: !this.editingData
            ? this.parseSelectValue(this.familySpecificFields.cable_type, this.cableTypeOptions)
            : this.familySpecificFields.cable_type,
          varName: 'cable_type',
          inputType: 'multiselect',
          disabled: false,
          required: true,
          options: this.cableTypeOptions,
          clearable: true,
          size: '100%'
        },
        {
          label: this.$i18n.t("HelixFactor") + '(0-1)',
          value: this.familySpecificFields.smc_helix,
          varName: 'smc_helix',
          inputType: 'number',
          disabled: false,
          step: '.01',
          min: '0',
          max: '1',
        },
        {
          label: this.$i18n.t("Capacity"),
          value: this.familySpecificFields.smc_capacity,
          varName: 'smc_capacity',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '0',
        },
        {
          label: this.$i18n.t("Diameter") + this.getUnitLabel("m"),
          value: this.familySpecificFields.smc_diameter,
          varName: 'smc_diameter',
          inputType: 'number',
          disabled: false,
          min: '0',
        },
        {
          label: this.$i18n.t("InstallationType"),
          value: !this.editingData
            ? this.parseSelectValue(this.familySpecificFields.installation_type, this.installationTypeOptions)
            : this.familySpecificFields.installation_type,
          varName: 'installation_type',
          inputType: 'multiselect',
          disabled: false,
          required: true,
          options: this.installationTypeOptions,
          clearable: false,
          size: '100%'
        },
        {
          label: this.$i18n.t("SagFactor") + '(0-1)',
          value: this.familySpecificFields.smc_sag_factor,
          varName: 'smc_sag_factor',
          inputType: 'number',
          disabled: false,
          step: '.01',
          min: '0',
          max: '1',
        },
        {
          label: this.$i18n.tc("Module", 2),
          value: this.familySpecificFields.smc_module,
          varName: 'smc_module',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '0',
        },
        {
          label: this.$i18n.t("MaxReelLength") + this.getUnitLabel("m"),
          value: this.familySpecificFields.smc_max_cable_length,
          varName: 'smc_max_cable_length',
          inputType: 'number',
          disabled: false,
          min: '0',
        },
        {
          label: this.$i18n.tc("Role", 2),
          value: !this.editingData
            ? this.parseSelectValue(this.familySpecificFields.logical_subtypes, this.logicalSubtypeOptions)
            : this.familySpecificFields.logical_subtypes,
          varName: 'logical_subtypes',
          inputType: 'multiselect',
          disabled: false,
          options: this.logicalSubtypeOptions,
          clearable: false,
          size: '100%'
        },
        {
          label: this.$i18n.t("LossValue"),
          value: this.lossValue,
          varName: 'sm_loss_value',
          inputType: 'number',
          disabled: false,
          step: '.00001',
          min: '0',
          size: '100%'
        },
      ]
    },
  },

  methods: {
    /**
     * Validations for the various required fields
     *
     * @return {boolean} indicating if the fields are valid
     */
    requiredFieldsValidation() {
      let helixAndSagAreValid = true;

      if (!!this.familySpecificFields.smc_helix) {
        helixAndSagAreValid = helixAndSagAreValid && this.familySpecificFields.smc_helix >= 0 && this.familySpecificFields.smc_helix <= 1;
      }
      if (!!this.familySpecificFields.smc_sag_factor) {
        helixAndSagAreValid = helixAndSagAreValid && this.familySpecificFields.smc_sag_factor >= 0 && this.familySpecificFields.smc_sag_factor <= 1;
      }

      return (
        !!this.familySpecificFields.cable_type && !!this.familySpecificFields.cable_type.length > 0 &&
        !!this.familySpecificFields.installation_type && !!this.familySpecificFields.installation_type.length > 0 &&
        !!this.familySpecificFields.smc_capacity && this.familySpecificFields.smc_capacity >= 0 &&
        !!this.familySpecificFields.smc_module && this.familySpecificFields.smc_module >= 0 &&
        helixAndSagAreValid
      );
    },

    /**
     * Loads the different types of cable options and assigns them to the attribute cableTypeOptions
     */
     async loadCableTypeOptions() {
      try {
        let cableTypeUnprocessed = await this.getList(
          "list_infra_type_log",
          this.familySpecificFields.cable_type,
          true
        );

        let codeToDelete
        cableTypeUnprocessed.forEach((item, index) => {
          if(item.libelle === 'Backhaul' || item.client_label === 'Backhaul'){
            codeToDelete = index
          }
          if(["5", "6"].includes(item.code)){
            item.deleted = false;
          }
        })

        if (codeToDelete) {
          cableTypeUnprocessed.splice(codeToDelete, 1);
        }

        this.cableTypeOptions = cableTypeUnprocessed;
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * Loads the different logical subtypes of cables and assigns them to the attribute logicalSubtypeOptions
     */
    async loadLogicalSubtypeOptions() {
      try {
        this.logicalSubtypeOptions = await this.getList(
          "list_cb_logical_subtype",
          this.familySpecificFields.logical_subtypes,
          false
        );
      } catch (e) {
        console.error(e);
      }
    },
  }
};
</script>

<style scoped>
</style>


// WEBPACK FOOTER //
// src/newComponents/StockManagement/SpecificMaterialSections/MaterialFieldsCable.vue